import { ArrowUpRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { BRAND } from '@/brand';
import { BrandMark } from '@/components/common/BrandMark';
import { LanguageSwitcher } from '@/components/common/LanguageSwitcher';
import { LegalLinks } from '@/components/common/LegalLinks';
import { SocialLinks } from '@/components/common/SocialLinks';
import { Button } from '@/components/ui/Button';
import { ROUTES } from '@/router';
import { useLocalizedPath } from '@/seo/useLocalePath';
import { useAuthStore } from '@/stores/authStore';

/**
 * The header of the public pages outside the landing (the results page, the legal pages): the
 * brand home link, the language menu, and sign-in / create account — or the way into the app
 * for a signed-in visitor.
 */
export function PublicHeader(): JSX.Element {
  const { t } = useTranslation();
  const signedIn = useAuthStore((state) => state.isAuthenticated);
  const bootstrapping = useAuthStore((state) => state.isBootstrapping);
  const localized = useLocalizedPath();
  const start = localized(signedIn ? ROUTES.dashboard : ROUTES.register);

  return (
    <header className="border-b border-border-subtle">
      <div className="mx-auto flex max-w-[1400px] items-center justify-between gap-3 px-4 py-3 sm:px-6">
        <Link
          to={localized(ROUTES.home)}
          className="flex items-center gap-2 rounded-sm text-ink focus-visible:outline-none focus-visible:shadow-focus"
          aria-label={BRAND.platform}
        >
          <BrandMark size={24} />
          <span className="text-title">{BRAND.platform}</span>
        </Link>
        <div className="flex items-center gap-2">
          <LanguageSwitcher />
          {!signedIn && !bootstrapping && (
            <Button asChild variant="ghost" size="sm" className="hidden sm:inline-flex">
              <Link to={localized(ROUTES.login)}>{t('nexus.landing.login')}</Link>
            </Button>
          )}
          <Button asChild variant="primary" size="sm">
            <Link to={start}>
              {signedIn ? t('nexus.landing.enter') : t('nexus.landing.start')}
              <ArrowUpRight size={14} aria-hidden="true" />
            </Link>
          </Button>
        </div>
      </div>
    </header>
  );
}

/**
 * The footer of those pages: the brand, its social accounts, the legal pages and the risk line —
 * left out (`risk={false}`) where the page already shows it higher up.
 */
export function PublicFooter({ risk = true }: { risk?: boolean }): JSX.Element {
  const { t } = useTranslation();
  return (
    <footer className="border-t border-border-subtle">
      <div className="mx-auto flex max-w-[1400px] flex-col gap-3 px-4 py-6 text-caption text-ink-tertiary sm:flex-row sm:items-end sm:justify-between sm:px-6">
        <div className="space-y-2">
          <span>
            © {new Date().getFullYear()} {BRAND.platform}
          </span>
          <SocialLinks className="flex items-center gap-3 text-ink-secondary [&_a:hover]:text-ink" />
        </div>
        <div className="max-w-2xl space-y-2 sm:text-right">
          <LegalLinks className="flex flex-wrap gap-x-4 gap-y-1 text-ink-secondary sm:justify-end [&_a:hover]:text-ink [&_a:hover]:underline" />
          {risk && <p>{t('legal.riskLine')}</p>}
        </div>
      </div>
    </footer>
  );
}
